import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const userPositionIcon = L.divIcon({
  className: 'user-position-marker',
  html: `
    <div style="position:relative;width:20px;height:20px;">
      <div style="
        position:absolute;inset:0;
        border-radius:50%;
        background:rgba(59,130,246,0.25);
        animation:user-pos-pulse 1.8s ease-out infinite;
      "></div>
      <div style="
        position:absolute;top:50%;left:50%;
        transform:translate(-50%,-50%);
        width:14px;height:14px;
        border-radius:50%;
        background:#3b82f6;
        border:2.5px solid #fff;
        box-shadow:0 1px 6px rgba(59,130,246,0.6);
      "></div>
    </div>
  `,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const WALKING_TOUR_COLOR = '#a855f7';

function createCircleIcon(color, points, imageUrl, hidePoints, ringColor = color) {
  const inner = imageUrl
    ? `<div style="
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background-image: url('${imageUrl.replace(/'/g, "\\'")}');
        background-size: cover;
        background-position: center;
      "></div>`
    : `<div style="
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: rgba(255,255,255,0.35);
      "></div>`;

  const badge = hidePoints ? '' : `<div style="
    position: absolute;
    top: -5px;
    right: -7px;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    border-radius: 10px;
    background: #0f0f23;
    color: ${color};
    border: 2px solid ${color};
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Inter, sans-serif;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    box-shadow: 0 1px 5px rgba(0,0,0,0.6);
    white-space: nowrap;
  ">${points}</div>`;

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position: relative; width: 44px; height: 44px;">
      <div style="
        background: ${color};
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: ${ringColor === color ? 3 : 5}px solid ${ringColor};
        box-shadow: ${ringColor === color
          ? '0 2px 10px rgba(0,0,0,0.4)'
          : `0 0 0 4px rgba(168,85,247,0.45), 0 0 18px 4px rgba(168,85,247,0.9)`};
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      ">${inner}</div>
      ${badge}
    </div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -24],
  });
}

function createGoalIcon(isCompleted) {
  const ringColor = isCompleted ? '#10b981' : '#a855f7';
  return L.divIcon({
    className: 'custom-marker goal-marker',
    html: `<div style="position: relative; width: 52px; height: 52px;">
      <div style="
        background: #1a0a2e;
        width: 52px;
        height: 52px;
        border-radius: 50%;
        border: 3px solid ${ringColor};
        box-shadow: 0 0 14px rgba(168,85,247,0.7), 0 2px 10px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        line-height: 1;
      ">🏁</div>
    </div>`,
    iconSize: [52, 52],
    iconAnchor: [26, 26],
    popupAnchor: [0, -28],
  });
}

function effectivePoints(basePoints, priorCompletions) {
  if (priorCompletions === 0) return basePoints;
  if (priorCompletions === 1) return Math.floor(basePoints * 0.7);
  return Math.floor(basePoints * 0.5);
}

const REYKJAVIK_CENTER = [64.1466, -21.9300];

// On first load, zoom the map so every stop is on screen. TEST stops are left
// out so local test stops in Oslo don't zoom the map out to all of Northern Europe.
function FitToStops({ stops }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current) return;
    const points = stops
      .filter(s => !s.title?.startsWith('TEST'))
      .map(s => [s.latitude, s.longitude]);
    if (points.length === 0) return;
    fitted.current = true;
    map.fitBounds(points, { padding: [40, 40], maxZoom: 16, animate: false });
  }, [map, stops]);
  return null;
}

function MapEventHandler({ onViewChange }) {
  useMapEvents({
    moveend(e) {
      const { lat, lng } = e.target.getCenter();
      const zoom = e.target.getZoom();
      onViewChange(lat, lng, zoom);
    },
    zoomend(e) {
      const { lat, lng } = e.target.getCenter();
      const zoom = e.target.getZoom();
      onViewChange(lat, lng, zoom);
    },
  });
  return null;
}

export default function MapView({ stops, teamId, onStopClick, userPosition, stopRadius = 70, hidePoints = false, initialCenter, initialZoom, onViewChange, goalStopId }) {
  const mapRef = useRef(null);

  const handleLocate = () => {
    if (!mapRef.current || !userPosition) return;
    mapRef.current.flyTo([userPosition.lat, userPosition.lng], 17, { duration: 1.2 });
  };

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <MapContainer
        ref={mapRef}
        center={initialCenter || REYKJAVIK_CENTER}
        zoom={initialZoom || 15}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        {onViewChange && <MapEventHandler onViewChange={onViewChange} />}
        {!initialCenter && <FitToStops stops={stops} />}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {userPosition && (
          <Marker
            position={[userPosition.lat, userPosition.lng]}
            icon={userPositionIcon}
            zIndexOffset={1000}
            interactive={false}
          />
        )}

        {stops.map(stop => {
          const isCompleted = stop.completed_by_teams?.includes(teamId);
          const isGoal = goalStopId != null && stop.id === goalStopId;
          const color = isCompleted ? '#10b981' : '#f59e0b';
          const otherCompletions = (stop.completed_by_teams || []).filter(id => id !== teamId).length;
          const displayPoints = effectivePoints(stop.points, otherCompletions);
          const icon = isGoal
            ? createGoalIcon(isCompleted)
            : createCircleIcon(color, displayPoints, stop.image_url, hidePoints,
              stop.walking_tour ? WALKING_TOUR_COLOR : color);
          const circleColor = stop.walking_tour ? WALKING_TOUR_COLOR : '#f59e0b';

          return (
            <React.Fragment key={stop.id}>
              <Circle
                center={[stop.latitude, stop.longitude]}
                radius={stopRadius}
                pathOptions={isGoal ? {
                  color: '#a855f7',
                  fillColor: '#a855f7',
                  fillOpacity: 0.12,
                  weight: 2,
                  opacity: 0.7,
                } : {
                  color: circleColor,
                  fillColor: circleColor,
                  fillOpacity: stop.walking_tour ? 0.22 : 0.15,
                  weight: stop.walking_tour ? 3 : 2,
                  opacity: stop.walking_tour ? 0.9 : 0.6,
                }}
              />
              <Marker
                position={[stop.latitude, stop.longitude]}
                icon={icon}
                zIndexOffset={isGoal ? 500 : 0}
                eventHandlers={{
                  click: () => onStopClick(stop),
                }}
              />
            </React.Fragment>
          );
        })}
      </MapContainer>

      {userPosition && (
        <button
          className="locate-me-btn"
          onClick={handleLocate}
          title="Sentrer på min posisjon"
          aria-label="Sentrer på min posisjon"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
        </button>
      )}
    </div>
  );
}
